import type { SkillDescriptor } from "./skills";

export interface SavedWorkflow {
  id: string;
  name: string;
  prompt: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}
export type WorkflowFetch = (path: string, init?: RequestInit) => Promise<Response>;
export interface WorkflowDraft {
  id: string;
  version?: number;
  name: string;
  prompt: string;
  originalName: string;
  originalPrompt: string;
}

export function workflowDraft(workflow?: SavedWorkflow, prompt = ""): WorkflowDraft {
  return {
    id: workflow?.id ?? crypto.randomUUID(),
    version: workflow?.version,
    name: workflow?.name ?? "",
    prompt: workflow?.prompt ?? prompt,
    originalName: workflow?.name ?? "",
    originalPrompt: workflow?.prompt ?? "",
  };
}
export function workflowDirty(draft: WorkflowDraft): boolean {
  return draft.name !== draft.originalName || draft.prompt !== draft.originalPrompt;
}
export function workflowValidation(name: string, prompt: string): string | null {
  if (!name.trim()) return "Give this workflow a name.";
  if (name.length > 80) return "Keep the name to 80 characters or fewer.";
  if (!prompt.trim()) return "Add the prompt you want to reuse.";
  if (prompt.length > 12_000) return "Keep the prompt to 12,000 characters or fewer.";
  return null;
}

export class WorkflowError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(message: string, status = 0, code = "") {
    super(message);
    this.name = "WorkflowError";
    this.status = status;
    this.code = code;
  }
}
const ERROR_MESSAGES: Record<string, string> = {
  workflow_version_conflict: "This workflow changed elsewhere. Your edits are still here; reload the saved version or save your text as a new workflow.",
  workflow_id_conflict: "This save may already have succeeded with different text. Your edits are still here. Refresh the list to check, or save this text as a new workflow.",
  workflow_deleted: "This workflow was deleted elsewhere. Your text is still here and can be saved as a new workflow.",
  workflow_not_found: "This workflow no longer exists. Your text is still here and can be saved as a new workflow.",
  workflow_limit: "You have 100 workflows. Delete one you no longer need, then try again.",
};
async function performRequest(fetcher: WorkflowFetch, path: string, init: RequestInit, signal: AbortSignal): Promise<unknown> {
  let response: Response;
  try { response = await fetcher(path, { ...init, signal }); }
  catch (error) {
    if (signal.aborted) throw error;
    throw new WorkflowError("Could not reach Workflows. Your edits are still here. Try again when connected.");
  }
  let data: unknown;
  try { data = await response.json(); }
  catch { throw new WorkflowError("Workflows returned an unreadable response. Your edits are still here; try again.", response.status); }
  if (!response.ok) {
    const code = data && typeof data === "object" && "code" in data ? String(data.code) : "";
    const fallback = response.status === 401 || response.status === 403
      ? "Sign in again to access Workflows. Your edits are still here."
      : "Could not save this change. Your edits are still here; try again.";
    throw new WorkflowError(ERROR_MESSAGES[code] ?? fallback, response.status, code);
  }
  return data;
}
/** Deadline covers token lookup/fetch AND JSON decoding, even if a fetcher ignores abort. */
export async function workflowRequest(fetcher: WorkflowFetch, path: string, init: RequestInit, signal: AbortSignal, timeoutMs = 20_000): Promise<unknown> {
  if (signal.aborted) throw new WorkflowError("Request cancelled.");
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel = () => {};
  const boundary = new Promise<never>((_resolve, reject) => {
    cancel = () => { reject(new WorkflowError("Request cancelled.")); controller.abort(); };
    signal.addEventListener("abort", cancel, { once: true });
    timer = setTimeout(() => {
      reject(new WorkflowError("The request timed out. Its outcome is unconfirmed. Your edits are still here; retry the same change to check it."));
      controller.abort();
    }, timeoutMs);
  });
  try { return await Promise.race([performRequest(fetcher, path, init, controller.signal), boundary]); }
  finally { clearTimeout(timer); signal.removeEventListener("abort", cancel); }
}
export function readWorkflow(value: unknown): SavedWorkflow {
  if (!value || typeof value !== "object") throw new WorkflowError("Invalid workflow response. Please refresh.");
  const row = value as Partial<SavedWorkflow>;
  if (typeof row.id !== "string" || !/^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i.test(row.id)
    || typeof row.name !== "string" || typeof row.prompt !== "string"
    || workflowValidation(row.name, row.prompt) || !Number.isInteger(row.version) || (row.version ?? 0) < 1
    || typeof row.createdAt !== "string" || typeof row.updatedAt !== "string") {
    throw new WorkflowError("Invalid workflow response. Please refresh.");
  }
  return row as SavedWorkflow;
}
export function readWorkflowList(value: unknown): SavedWorkflow[] {
  if (!value || typeof value !== "object" || !("workflows" in value) || !Array.isArray(value.workflows)
    || value.workflows.length > 100) throw new WorkflowError("Invalid workflow list. Please retry.");
  const rows = value.workflows.map(readWorkflow);
  if (new Set(rows.map(row => row.id)).size !== rows.length) throw new WorkflowError("Invalid workflow list. Please retry.");
  return rows;
}

/** One dialog generation. An aborted/late request cannot update a reopened dialog. */
export function workflowRequestScope() {
  let generation = 0;
  let controller = new AbortController();
  return {
    begin() {
      controller.abort();
      controller = new AbortController();
      const current = ++generation;
      return { signal: controller.signal, current: () => current === generation && !controller.signal.aborted };
    },
    cancel() { ++generation; controller.abort(); },
  };
}

export interface WorkflowSlash { start: number; end: number; query: string }
export function workflowSlashAt(text: string, cursor: number): WorkflowSlash | null {
  const match = /(?:^|\s)\/([a-z\d-]*)$/i.exec(text.slice(0, cursor));
  return match ? { start: cursor - match[1].length - 1, end: cursor, query: match[1] } : null;
}
export function workflowSkillSuggestions(skills: SkillDescriptor[], query: string): SkillDescriptor[] {
  const q = query.toLowerCase();
  const seen = new Set<string>();
  return skills.filter(skill => {
    if (skill.group === "Built-in" || ["notify-julian", "client-proposal", "notify-farhan", "notify-all"].includes(skill.name)
      || !/^[a-z\d][a-z\d-]*$/i.test(skill.name) || seen.has(skill.name)) return false;
    seen.add(skill.name);
    return skill.name.toLowerCase().includes(q);
  }).sort((a, b) => Number(!a.name.toLowerCase().startsWith(q)) - Number(!b.name.toLowerCase().startsWith(q))).slice(0, 6);
}
export function insertWorkflowSkill(prompt: string, slash: WorkflowSlash, name: string) {
  const token = `/${name} `;
  return { prompt: prompt.slice(0, slash.start) + token + prompt.slice(slash.end), cursor: slash.start + token.length };
}
